import type { PoolCalculateResponse, PoolApplication } from '@/api/calculators/types';
import { fmt, rs } from './constants';
import { Section, ProfileRow, CmpRow, SavingsRow, TcoSection } from './PoolUi';

export function RoiPageView({
  response,
  application,
}: {
  response: PoolCalculateResponse;
  application: PoolApplication;
}) {
  const { sizing, roi_full } = response;
  const opt2 = sizing.recommendation.option_2_with_redundancy;
  const c = roi_full.year_1_costs;
  const s = roi_full.annual_savings_year_1;
  const maxSav = Math.max(s.vs_lpg, s.vs_diesel, s.vs_electric, 1);

  return (
    <div
      data-testid="pool-roi-view"
      className="pool-print-roi-page space-y-4"
    >
      {/* Header */}
      <div className="rounded-lg bg-gradient-to-br from-slate-900 to-slate-700 p-4 text-white">
        <div className="font-mono text-[10px] font-bold uppercase tracking-widest opacity-60">
          Quotation · Page 2 · ROI Analysis
        </div>
        <div className="mt-1 font-serif text-xl font-bold leading-tight">
          {opt2.qty_working} × {opt2.model_id} ·{' '}
          {application.charAt(0).toUpperCase() + application.slice(1)} Project
        </div>
        <div className="mt-1 text-xs opacity-90">
          {roi_full.inputs_used.design_ambient_c}°C design ·{' '}
          {fmt(sizing.pool.volume_m3, 1)} m³ · target{' '}
          {roi_full.inputs_used.target_temp_c}°C
        </div>
      </div>

      {/* 1. Operating Profile */}
      <Section title="1. Operating Profile">
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
          <ProfileRow
            label="Days operational/year"
            value={`${roi_full.operating_profile.days_per_year}`}
            sub={`(${roi_full.operating_profile.derived_from})`}
            warn={roi_full.operating_profile.derived_from === 'custom'}
          />
          <ProfileRow
            label="Hours/day at temp"
            value={`${roi_full.operating_profile.hours_per_day} hrs`}
          />
          <ProfileRow
            label="Heat-up frequency"
            value={roi_full.operating_profile.heat_up_frequency}
          />
          <ProfileRow
            label="Annual heat to pool"
            value={`${fmt(roi_full.operating_profile.annual_heat_delivered_kwh, 0)} kWh`}
          />
        </div>
        <div className="mt-2 border-t border-stone-200 pt-2 text-[11px] text-slate-500">
          {roi_full.operating_profile.derivation_note}
        </div>
      </Section>

      {/* 2. Year-1 Cost Comparison */}
      <Section title="2. Year-1 Cost Comparison">
        <table className="w-full text-[11.5px]">
          <thead>
            <tr className="border-b border-stone-200 text-left font-mono text-[9.5px] uppercase tracking-wider text-slate-500">
              <th className="py-2"></th>
              <th className="py-2 text-center text-emerald-700 bg-emerald-50">
                Heat Pump
              </th>
              <th className="py-2 text-center">LPG</th>
              <th className="py-2 text-center">Diesel</th>
              <th className="py-2 text-center">Electric</th>
            </tr>
          </thead>
          <tbody>
            <CmpRow
              label="Efficiency / COP"
              hp={`${roi_full.annual_avg_cop.toFixed(1)} avg`}
              lpg="85%"
              diesel="80%"
              electric="95%"
            />
            <CmpRow
              label="Year-1 energy"
              hp={rs(c.heat_pump.annual_cost)}
              lpg={rs(c.lpg_boiler.annual_cost)}
              diesel={rs(c.diesel_boiler.annual_cost)}
              electric={rs(c.electric_immersion.annual_cost)}
            />
            <CmpRow
              label="Monthly energy"
              hp={rs(c.heat_pump.monthly_cost)}
              lpg={rs(c.lpg_boiler.monthly_cost)}
              diesel={rs(c.diesel_boiler.monthly_cost)}
              electric={rs(c.electric_immersion.monthly_cost)}
            />
            <CmpRow
              label="Maintenance/yr"
              hp={rs(c.heat_pump.maintenance_per_year)}
              lpg={rs(c.lpg_boiler.maintenance_per_year)}
              diesel={rs(c.diesel_boiler.maintenance_per_year)}
              electric={rs(c.electric_immersion.maintenance_per_year)}
            />
            <CmpRow
              label="Capex (indicative)"
              hp={rs(c.heat_pump.capex)}
              lpg={rs(c.lpg_boiler.capex)}
              diesel={rs(c.diesel_boiler.capex)}
              electric={rs(c.electric_immersion.capex)}
            />
          </tbody>
        </table>
      </Section>

      {/* 3. Annual Savings (Year-1) */}
      <Section title="3. Annual Savings (Year-1)">
        <SavingsRow
          label="vs LPG Boiler"
          value={s.vs_lpg}
          pct={(s.vs_lpg / maxSav) * 100}
          color="#92400E"
          payback={s.payback_years.vs_lpg}
        />
        <SavingsRow
          label="vs Diesel Boiler"
          value={s.vs_diesel}
          pct={(s.vs_diesel / maxSav) * 100}
          color="#7C2D12"
          payback={s.payback_years.vs_diesel}
        />
        <SavingsRow
          label="vs Electric Immersion"
          value={s.vs_electric}
          pct={(s.vs_electric / maxSav) * 100}
          color="#1E40AF"
          payback={s.payback_years.vs_electric}
        />
      </Section>

      {/* 4. 5-Year TCO */}
      <TcoSection
        title="4. 5-Year Total Cost of Ownership"
        years={5}
        tco={roi_full.tco_5yr}
      />

      {/* 5. 10-Year TCO */}
      <TcoSection
        title="5. 10-Year Total Cost of Ownership"
        years={10}
        tco={roi_full.tco_10yr}
      />

      {/* Compounding callout */}
      {roi_full.tco_5yr.net_savings_vs_lpg > 0 &&
        roi_full.tco_10yr.net_savings_vs_lpg > 0 && (
          <div className="rounded-lg border border-stone-200 bg-gradient-to-br from-emerald-50 to-blue-50 p-3">
            <div className="font-mono text-[10.5px] font-bold uppercase tracking-wide text-slate-700">
              Heat pump advantage compounds
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div className="rounded-md bg-white p-2.5">
                <div className="text-[10.5px] font-semibold uppercase text-slate-500">
                  5-yr saving vs LPG
                </div>
                <div className="mt-1 font-mono text-lg font-bold text-emerald-700">
                  {rs(roi_full.tco_5yr.net_savings_vs_lpg)}
                </div>
              </div>
              <div className="rounded-md bg-white p-2.5">
                <div className="text-[10.5px] font-semibold uppercase text-slate-500">
                  10-yr saving vs LPG
                </div>
                <div className="mt-1 font-mono text-lg font-bold text-emerald-700">
                  {rs(roi_full.tco_10yr.net_savings_vs_lpg)}
                </div>
              </div>
            </div>
            <div className="mt-2 text-[11.5px] text-slate-600">
              10-year saving ={' '}
              <strong>
                {(
                  roi_full.tco_10yr.net_savings_vs_lpg /
                  roi_full.tco_5yr.net_savings_vs_lpg
                ).toFixed(1)}
                ×
              </strong>{' '}
              the 5-year — fuel inflation compounds yearly.
            </div>
          </div>
        )}

      {/* 6. Cooling bonus */}
      {roi_full.cooling_bonus_applies && (
        <Section title="6. Cooling-Season Bonus">
          <div className="text-sm font-semibold">
            Reverse-cycle pool cooling:{' '}
            <span className="font-mono text-emerald-700">
              {rs(roi_full.cooling_bonus_per_year)}/yr
            </span>
          </div>
          <div className="mt-1 text-[11.5px] text-slate-600">
            {roi_full.disclaimer.cooling_note}
          </div>
        </Section>
      )}

      {/* Methodology */}
      <div className="rounded-md border-l-[3px] border-slate-400 bg-stone-100 px-3 py-2 text-[11px] leading-relaxed text-slate-700">
        <div className="font-mono font-bold uppercase tracking-wide text-slate-900">
          Methodology
        </div>
        <ul className="mt-1 list-disc pl-5">
          <li>{roi_full.disclaimer.cover_assumed}</li>
          <li>{roi_full.disclaimer.cop_basis}</li>
          <li>{roi_full.disclaimer.escalation_basis}</li>
          <li>{roi_full.disclaimer.days_basis}</li>
          <li>{roi_full.disclaimer.capex_basis}</li>
        </ul>
      </div>
    </div>
  );
}

// ─── Reverse-flow inputs panel ──────────────────────────────────────────────
